<template>
  <div class="ps-panel">
    <div class="ps-panel-hd">
      <h2>Notificações</h2>
      <p>Receba alertas mesmo com o aplicativo fechado, em qualquer dispositivo.</p>
    </div>

    <!-- Status atual -->
    <div class="notif-status-card" :class="webPushActive ? 'notif-status-card--active' : pushEnabled ? 'notif-status-card--basic' : 'notif-status-card--off'">
      <div class="notif-status-icon">
        <svg v-if="webPushActive" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        <svg v-else-if="pushEnabled" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7M9 21a3 3 0 0 0 6 0m6.43-6.43A9.07 9.07 0 0 0 18 9"/></svg>
      </div>
      <div class="notif-status-text">
        <span class="notif-status-label" v-if="webPushActive">Notificações em background ativas</span>
        <span class="notif-status-label" v-else-if="pushEnabled">Apenas com app aberto</span>
        <span class="notif-status-label" v-else>Notificações desabilitadas</span>
        <span class="notif-status-desc" v-if="webPushActive">Você receberá alertas mesmo com o navegador fechado.</span>
        <span class="notif-status-desc" v-else-if="pushEnabled">Ativas somente enquanto esta aba estiver aberta no navegador.</span>
        <span class="notif-status-desc" v-else>Ative abaixo para receber alertas do sistema.</span>
      </div>
    </div>

    <!-- Navegador bloqueou -->
    <div v-if="pushPermission === 'denied'" class="notif-alert notif-alert--warn">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span>O navegador bloqueou as notificações. Clique no ícone de cadeado (🔒) na barra de endereço, vá em <strong>Configurações do site</strong> e mude <strong>Notificações</strong> para <em>Permitir</em>.</span>
    </div>

    <!-- Instrução iOS não-PWA -->
    <div v-if="isIOS && !isPWA && isSupported" class="notif-alert notif-alert--info">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      <div>
        <strong>iPhone / iPad:</strong> para receber notificações em background, instale o app na tela inicial:
        <ol class="notif-steps">
          <li>Toque em <strong>Compartilhar</strong> no Safari</li>
          <li>Selecione <strong>Adicionar à Tela de Início</strong></li>
          <li>Abra o app pela tela inicial e ative as notificações aqui</li>
        </ol>
        <p style="margin:4px 0 0;font-size:12px;color:var(--ink-muted)">Exige iOS 16.4 ou superior.</p>
      </div>
    </div>

    <!-- Toggle -->
    <div v-if="isSupported && pushPermission !== 'denied'" class="notif-toggle-row">
      <div class="notif-toggle-info">
        <span class="notif-toggle-label">Receber notificações</span>
        <span class="notif-toggle-desc">
          <span v-if="isWebPushSupported">Alertas em background via Web Push</span>
          <span v-else>Alertas enquanto o app estiver aberto</span>
        </span>
      </div>
      <button type="button" class="push-toggle-btn" :class="{ active: pushEnabled }" role="switch" :aria-checked="pushEnabled" @click="togglePush">
        <span class="push-toggle-thumb" />
      </button>
    </div>

    <div v-if="pushPermission === 'default' && isSupported" class="ps-panel-ft">
      <button class="btn btn-primary btn-login" type="button" @click="askPushPermission">
        Permitir notificações
      </button>
    </div>

    <!-- Compatibilidade colapsável -->
    <button class="compat-toggle" type="button" @click="showCompat = !showCompat">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      Consultar compatibilidade com o seu sistema
      <svg class="compat-chevron" :class="{ open: showCompat }" width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 4l3.5 3.5L9 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>

    <div v-if="showCompat" class="compat-text">
      <p><strong>Android (Chrome)</strong> — notificações em background completo, mesmo com o Chrome fechado. O sistema operacional mantém uma conexão permanente com o servidor de push do Google (FCM).</p>
      <p><strong>Windows (Chrome / Edge)</strong> — funciona com o navegador minimizado ou em segundo plano. O browser precisa estar rodando, mas não precisa estar em foco.</p>
      <p><strong>Mac (Chrome / Firefox)</strong> — mesmo comportamento do Windows. Safari no Mac suporta a partir do macOS 13 Ventura.</p>
      <p><strong>iPhone / iPad (Safari)</strong> — requer iOS 16.4 ou superior <em>e</em> o app instalado na tela inicial como PWA. Sem o PWA instalado, o Safari não permite push em background. Veja as instruções acima para instalar.</p>
      <p class="compat-note">O seu dispositivo atual está destacado na lista. Notificações em background usam o protocolo Web Push (VAPID) — o mesmo mecanismo que WhatsApp e outros apps utilizam.</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { usePushNotifications } from '@/composables/usePushNotifications'

export default defineComponent({
  name: 'ProfileNotificacoesTab',
  setup() {
    const {
      isSupported, isWebPushSupported, permission: pushPermission,
      enabled: pushEnabled, webPushActive, isIOS, isPWA,
      requestPermission, setEnabled, checkWebPushActive,
    } = usePushNotifications()

    const showCompat = ref(false)

    onMounted(() => { checkWebPushActive() })

    const askPushPermission = async () => { await requestPermission() }
    const togglePush = async () => { await setEnabled(!pushEnabled.value) }

    return {
      isSupported, isWebPushSupported, pushPermission, pushEnabled, webPushActive,
      isIOS, isPWA, showCompat, askPushPermission, togglePush,
    }
  }
})
</script>

<style scoped lang="scss">
.ps-panel-hd { margin-bottom: 20px; h2 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 4px; } p { font-size: 13px; color: var(--ink-muted); margin: 0; } }

.notif-status-card {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 18px; border-radius: 12px; border: 1px solid; margin-bottom: 16px;
  &--active { background: #f0fdf4; border-color: #86efac; color: #166534; }
  &--basic  { background: #fffbeb; border-color: #fde68a; color: #92400e; }
  &--off    { background: var(--surface, rgba(31,30,26,.04)); border-color: rgba(31,30,26,.1); color: var(--ink-muted); }
}
.notif-status-icon { width: 36px; height: 36px; border-radius: 8px; background: rgba(0,0,0,.12); display: flex; align-items: center; justify-content: center; flex-shrink: 0; svg { stroke: #fff; } }
.notif-status-text { display: flex; flex-direction: column; gap: 2px; }
.notif-status-label { font-size: 13px; font-weight: 700; }
.notif-status-desc { font-size: 12px; opacity: .75; }

.notif-alert {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; border-radius: 10px; font-size: 13px; line-height: 1.5; margin-bottom: 16px;
  &--warn { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
  &--info { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
  svg { flex-shrink: 0; margin-top: 1px; }
}
.notif-steps { margin: 6px 0 0 0; padding-left: 18px; display: flex; flex-direction: column; gap: 3px; font-size: 13px; }

.notif-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px; border: 1px solid var(--border); border-radius: 10px; background: var(--surface, #fff); margin-bottom: 16px; }
.notif-toggle-info { display: flex; flex-direction: column; gap: 3px; }
.notif-toggle-label { font-size: 13px; font-weight: 700; color: var(--ink); }
.notif-toggle-desc { font-size: 12px; color: var(--ink-muted); }
.push-toggle-btn { position: relative; width: 40px; height: 22px; border-radius: 999px; border: none; background: rgba(31,30,26,.18); cursor: pointer; flex-shrink: 0; transition: background .2s; &.active { background: var(--accent, #0f4c81); } }
.push-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform .2s; pointer-events: none; .push-toggle-btn.active & { transform: translateX(18px); } }

.ps-panel-ft { margin-bottom: 16px; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid transparent; border-radius: var(--radius, 6px); padding: 6px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .12s; }
.btn-primary { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); &:hover { filter: brightness(1.1); } }
.btn-login { padding: 10px 22px; border-radius: 8px; }

.compat-toggle {
  display: flex; align-items: center; gap: 7px;
  background: none; border: none; cursor: pointer; padding: 0;
  font-size: 13px; color: var(--accent, #0f4c81); font-weight: 500;
  margin-top: 4px;
  &:hover { text-decoration: underline; }
  svg { flex-shrink: 0; }
}
.compat-chevron { transition: transform .2s; &.open { transform: rotate(180deg); } }

.compat-text {
  margin-top: 12px; padding: 14px 16px;
  background: var(--surface, #fff); border: 1px solid var(--border);
  border-radius: 10px; display: flex; flex-direction: column; gap: 10px;
  p { margin: 0; font-size: 13px; color: var(--ink-muted); line-height: 1.6; }
  strong { color: var(--ink); font-weight: 600; }
}
.compat-note { font-size: 12px !important; border-top: 1px solid var(--border); padding-top: 10px !important; margin-top: 2px; }
</style>
